import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaEnvelope, FaCheckCircle, FaSpinner, FaArrowLeft } from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { auth, sendVerificationEmail } from '../lib/firebase';
import logo from '../assets/logo.png';

const VerifyEmail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);

  // Check if user is verified periodically
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    // If already verified, redirect to dashboard
    if (currentUser.emailVerified) {
      setIsVerified(true);
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 2000);
      return;
    }

    // Check verification status every 3 seconds
    const interval = setInterval(async () => {
      try {
        await currentUser.reload();
        if (auth.currentUser?.emailVerified) {
          setIsVerified(true);
          clearInterval(interval);
          toast.success(t('auth.emailVerified', 'Email verified successfully!'));
          setTimeout(() => {
            navigate('/dashboard', { replace: true });
          }, 2000);
        }
      } catch (error) {
        // Silent fail - will retry in 3 seconds
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [currentUser, navigate, t]);

  const handleResendEmail = async () => {
    // Use auth.currentUser to get the fresh user object instead of potentially stale currentUser from context
    const user = auth.currentUser;
    
    if (!user) {
      toast.error(t('errors.userNotFound', 'No user found. Please log in again.'));
      navigate('/login');
      return;
    }

    // Check if user is already verified
    if (user.emailVerified) {
      toast.success(t('auth.alreadyVerified', 'Your email is already verified!'));
      setIsVerified(true);
      return;
    }

    setIsLoading(true);
    try {
      // Reload user to ensure we have fresh token
      await user.reload();
      
      // Check again after reload
      if (auth.currentUser?.emailVerified) {
        toast.success(t('auth.alreadyVerified', 'Your email is already verified!'));
        setIsVerified(true);
        return;
      }
      
      await sendVerificationEmail(auth.currentUser);
      toast.success(t('auth.verificationEmailSent', 'Verification email sent! Check your inbox.'));
    } catch (error) {
      console.error('Email verification error:', error);
      
      if (error.code === 'auth/too-many-requests') {
        toast.error(t('errors.tooManyRequests', 'Too many requests. Please wait a few minutes before trying again.'));
      } else if (error.code === 'auth/user-token-expired' || error.code === 'auth/invalid-user-token') {
        toast.error(t('errors.sessionExpired', 'Session expired. Please log in again.'));
        await logout();
        navigate('/login');
      } else if (error.code === 'auth/network-request-failed') {
        toast.error(t('errors.networkError', 'Network error. Please check your connection.'));
      } else {
        toast.error(t('errors.emailSendFailed', 'Failed to send verification email. Please try again.'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = async () => {
    await logout();
    navigate('/login');
  };

  if (isVerified) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F0F9FF] via-white to-[#EFF6FF] flex items-center justify-center p-4 relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-100 rounded-full -translate-x-1/2 -translate-y-1/2 opacity-30"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-100 rounded-full translate-x-1/2 translate-y-1/2 opacity-30"></div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 text-center relative z-10"
        >
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <FaCheckCircle className="w-10 h-10 text-green-600" />
          </motion.div>

          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {t('auth.emailVerifiedTitle', 'Email Verified!')}
          </h1>

          <p className="text-gray-600 mb-6 leading-relaxed">
            {t('auth.emailVerifiedDesc', 'Your email has been verified. Redirecting you to the dashboard...')}
          </p>

          <div className="flex items-center justify-center gap-2 text-blue-600">
            <FaSpinner className="w-4 h-4 animate-spin" />
            <span>{t('auth.redirecting', 'Redirecting...')}</span>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F0F9FF] via-white to-[#EFF6FF] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-blue-100 rounded-full -translate-x-1/2 -translate-y-1/2 opacity-30"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-100 rounded-full translate-x-1/2 translate-y-1/2 opacity-30"></div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 relative z-10"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
            className="flex justify-center mb-4"
          >
            <img src={logo} alt="CVLogs" className="w-16 h-16 object-contain" />
          </motion.div>

          {/* Email Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <FaEnvelope className="w-10 h-10 text-blue-600" />
          </motion.div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {t('auth.verifyEmailTitle', 'Verify Your Email')}
          </h1>

          <p className="text-gray-600">
            {t('auth.verifyEmailDesc', "We've sent a verification link to your email address. Please check your inbox and click the link to verify your account.")}
          </p>

          {currentUser?.email && (
            <p className="text-blue-600 font-medium mt-2">
              {currentUser.email}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={handleResendEmail}
            disabled={isLoading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <FaSpinner className="w-4 h-4 animate-spin" />
                {t('auth.sending', 'Sending...')}
              </>
            ) : (
              <>
                <FaEnvelope className="w-4 h-4" />
                {t('auth.resendVerificationEmail', 'Resend Verification Email')}
              </>
            )}
          </button>

          <button
            onClick={handleBackToLogin}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-4 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            <FaArrowLeft className="w-4 h-4" />
            {t('auth.backToLogin', 'Back to Login')}
          </button>
        </div>

        {/* Help text */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>{t('auth.checkSpamFolder', "Can't find the email? Check your spam folder.")}</p>
        </div>

        {/* Auto-check indicator */}
        <div className="mt-4 text-center text-xs text-gray-400 flex items-center justify-center gap-2">
          <FaSpinner className="w-3 h-3 animate-spin" />
          <span>{t('auth.autoCheckingVerification', 'Automatically checking verification status...')}</span>
        </div>
      </motion.div>
    </div>
  );
};

export default VerifyEmail;
